import React from 'react';
import { cn } from '@/utils/cn';
import { ASSET_GROUP_LIMITS } from '@/types/assetGroup';

interface AssetGroupTextAssetsStepProps {
  headlines: string[];
  longHeadline: string;
  descriptions: string[];
  businessName: string;
  onChangeHeadlines: (headlines: string[]) => void;
  onChangeLongHeadline: (longHeadline: string) => void;
  onChangeDescriptions: (descriptions: string[]) => void;
  onChangeBusinessName: (businessName: string) => void;
  errors?: {
    headlines?: string;
    longHeadline?: string;
    descriptions?: string;
    businessName?: string;
  };
}

export const AssetGroupTextAssetsStep: React.FC<AssetGroupTextAssetsStepProps> = ({
  headlines,
  longHeadline,
  descriptions,
  businessName,
  onChangeHeadlines,
  onChangeLongHeadline,
  onChangeDescriptions,
  onChangeBusinessName,
  errors,
}) => {
  const handleHeadlineChange = (index: number, value: string) => {
    const updated = [...headlines];
    updated[index] = value;
    onChangeHeadlines(updated);
  };

  const handleDescriptionChange = (index: number, value: string) => {
    const updated = [...descriptions];
    updated[index] = value;
    onChangeDescriptions(updated);
  };

  const addHeadline = () => {
    if (headlines.length < ASSET_GROUP_LIMITS.headline.max) {
      onChangeHeadlines([...headlines, '']);
    }
  };

  const removeHeadline = (index: number) => {
    if (headlines.length > ASSET_GROUP_LIMITS.headline.min) {
      onChangeHeadlines(headlines.filter((_, i) => i !== index));
    }
  };

  const addDescription = () => {
    if (descriptions.length < ASSET_GROUP_LIMITS.description.max) {
      onChangeDescriptions([...descriptions, '']);
    }
  };

  const removeDescription = (index: number) => {
    if (descriptions.length > ASSET_GROUP_LIMITS.description.min) {
      onChangeDescriptions(descriptions.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Text Assets</h3>
        <p className="text-sm text-gray-600">
          Add the text content for your asset group. Google will automatically combine these assets to create optimized ads.
        </p>
      </div>

      {/* Headlines */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Headlines <span className="text-red-500">*</span>
          </label>
          <span className="text-xs text-gray-500">
            {headlines.length}/{ASSET_GROUP_LIMITS.headline.max} (min {ASSET_GROUP_LIMITS.headline.min} required)
          </span>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          Short headlines (max {ASSET_GROUP_LIMITS.headline.charLimit} characters each). Used in search and display placements.
        </p>
        <div className="space-y-2">
          {headlines.map((headline, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={headline}
                onChange={(e) => handleHeadlineChange(index, e.target.value)}
                placeholder={`Headline ${index + 1}`}
                maxLength={ASSET_GROUP_LIMITS.headline.charLimit}
                className={cn(
                  'flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
                  { 'border-gray-300': !errors?.headlines, 'border-red-500': errors?.headlines }
                )}
              />
              <span className="text-xs text-gray-500 self-center min-w-[40px]">
                {headline.length}/{ASSET_GROUP_LIMITS.headline.charLimit}
              </span>
              {headlines.length > ASSET_GROUP_LIMITS.headline.min && (
                <button
                  type="button"
                  onClick={() => removeHeadline(index)}
                  className="text-red-600 hover:text-red-700 px-2"
                  aria-label={`Remove headline ${index + 1}`}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
        {errors?.headlines && <p className="mt-1 text-sm text-red-600">{errors.headlines}</p>}
        {headlines.length < ASSET_GROUP_LIMITS.headline.max && (
          <button
            type="button"
            onClick={addHeadline}
            className="mt-2 text-sm text-blue-600 hover:text-blue-700"
          >
            + Add headline
          </button>
        )}
      </div>

      {/* Long Headline */}
      <div>
        <label htmlFor="long-headline" className="block text-sm font-medium text-gray-700 mb-2">
          Long Headline <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-500 mb-2">
          A longer headline (max {ASSET_GROUP_LIMITS.longHeadline.charLimit} characters) used in larger ad formats.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            id="long-headline"
            value={longHeadline}
            onChange={(e) => onChangeLongHeadline(e.target.value)}
            placeholder="Enter a compelling long headline"
            maxLength={ASSET_GROUP_LIMITS.longHeadline.charLimit}
            className={cn(
              'flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
              { 'border-gray-300': !errors?.longHeadline, 'border-red-500': errors?.longHeadline }
            )}
          />
          <span className="text-xs text-gray-500 self-center min-w-[40px]">
            {longHeadline.length}/{ASSET_GROUP_LIMITS.longHeadline.charLimit}
          </span>
        </div>
        {errors?.longHeadline && (
          <p className="mt-1 text-sm text-red-600">{errors.longHeadline}</p>
        )}
      </div>

      {/* Descriptions */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Descriptions <span className="text-red-500">*</span>
          </label>
          <span className="text-xs text-gray-500">
            {descriptions.length}/{ASSET_GROUP_LIMITS.description.max} (min {ASSET_GROUP_LIMITS.description.min} required)
          </span>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          Describe your product or service (max {ASSET_GROUP_LIMITS.description.charLimit} characters each).
        </p>
        <div className="space-y-2">
          {descriptions.map((description, index) => (
            <div key={index} className="flex gap-2">
              <textarea
                value={description}
                onChange={(e) => handleDescriptionChange(index, e.target.value)}
                placeholder={`Description ${index + 1}`}
                maxLength={ASSET_GROUP_LIMITS.description.charLimit}
                rows={2}
                className={cn(
                  'flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none',
                  { 'border-gray-300': !errors?.descriptions, 'border-red-500': errors?.descriptions }
                )}
              />
              <span className="text-xs text-gray-500 self-start pt-2 min-w-[40px]">
                {description.length}/{ASSET_GROUP_LIMITS.description.charLimit}
              </span>
              {descriptions.length > ASSET_GROUP_LIMITS.description.min && (
                <button
                  type="button"
                  onClick={() => removeDescription(index)}
                  className="text-red-600 hover:text-red-700 px-2 self-start pt-2"
                  aria-label={`Remove description ${index + 1}`}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
        {errors?.descriptions && <p className="mt-1 text-sm text-red-600">{errors.descriptions}</p>}
        {descriptions.length < ASSET_GROUP_LIMITS.description.max && (
          <button
            type="button"
            onClick={addDescription}
            className="mt-2 text-sm text-blue-600 hover:text-blue-700"
          >
            + Add description
          </button>
        )}
      </div>

      {/* Business Name */}
      <div>
        <label htmlFor="business-name" className="block text-sm font-medium text-gray-700 mb-2">
          Business Name <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Your business or brand name (max {ASSET_GROUP_LIMITS.businessName.charLimit} characters).
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            id="business-name"
            value={businessName}
            onChange={(e) => onChangeBusinessName(e.target.value)}
            placeholder="Your Business Name"
            maxLength={ASSET_GROUP_LIMITS.businessName.charLimit}
            className={cn(
              'flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
              { 'border-gray-300': !errors?.businessName, 'border-red-500': errors?.businessName }
            )}
          />
          <span className="text-xs text-gray-500 self-center min-w-[40px]">
            {businessName.length}/{ASSET_GROUP_LIMITS.businessName.charLimit}
          </span>
        </div>
        {errors?.businessName && (
          <p className="mt-1 text-sm text-red-600">{errors.businessName}</p>
        )}
      </div>
    </div>
  );
};
